import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocalizedCurrency, LocalizedDate } from '../localization/LocalizedComponents';

interface Commission {
  id: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  deadline?: string;
  createdAt: string;
  acceptedAt?: string;
  completedAt?: string;
  deliveredAt?: string;
  requirements?: string;
  notes?: string;
  rejectReason?: string;
  patron: {
    username: string;
    avatar: string | null;
    email: string;
  };
  creator: {
    username: string;
    avatar: string | null;
  };
  deliverables: Array<{
    id: string;
    fileUrl: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    description?: string;
    uploadedAt: string;
  }>;
}

export const CommissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [commission, setCommission] = useState<Commission | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchCommissionDetails();
    fetchCurrentUser();
  }, [id]);

  const fetchCurrentUser = async () => {
    // Get current user ID from token or API
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserId(user.userId || '');
  };

  const fetchCommissionDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      setCommission(data.commission);
    } catch (error) {
      console.error('Failed to fetch commission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Commission accepted! Payment is now in escrow.');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to accept commission');
      }
    } catch (error) {
      console.error('Failed to accept:', error);
      alert('Failed to accept commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: rejectReason })
      });

      if (response.ok) {
        alert('Commission rejected');
        setShowRejectModal(false);
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to reject commission');
      }
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('Failed to reject commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkInProgress = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Commission marked as in progress');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update:', error);
      alert('Failed to update commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkDelivered = async () => {
    if (!commission?.deliverables || commission.deliverables.length === 0) {
      alert('Please upload deliverables before marking as delivered');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/deliver`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Commission marked as delivered. Awaiting client approval.');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to deliver commission');
      }
    } catch (error) {
      console.error('Failed to deliver:', error);
      alert('Failed to deliver commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!confirm('Are you sure you want to approve this commission? Payment will be released to the creator.')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Commission approved! Payment has been released to the creator.');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to approve commission');
      }
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve commission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDispute = async () => {
    const reason = prompt('Please describe the issue with this commission:');
    if (!reason) return;

    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/dispute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        alert('Dispute opened. Our support team will review this case.');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to open dispute');
      }
    } catch (error) {
      console.error('Failed to dispute:', error);
      alert('Failed to open dispute');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this commission?')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`/api/commissions/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Commission cancelled');
        fetchCommissionDetails();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to cancel commission');
      }
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('Failed to cancel commission');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusTimeline = () => {
    if (!commission) return [];

    const timeline = [
      { label: 'Requested', status: 'PENDING', date: commission.createdAt, active: true },
      { label: 'Accepted', status: 'ACCEPTED', date: commission.acceptedAt, active: !!commission.acceptedAt },
      { label: 'In Progress', status: 'IN_PROGRESS', active: ['IN_PROGRESS', 'COMPLETED', 'DELIVERED', 'APPROVED'].includes(commission.status) },
      { label: 'Delivered', status: 'DELIVERED', date: commission.deliveredAt, active: ['DELIVERED', 'APPROVED'].includes(commission.status) },
      { label: 'Completed', status: 'APPROVED', date: commission.completedAt, active: commission.status === 'APPROVED' }
    ];

    return timeline;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!commission) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Commission Not Found</h1>
        <Link to="/commissions" className="text-orange-600 hover:text-orange-700">
          Back to Commissions
        </Link>
      </div>
    );
  }

  const isCreator = currentUserId === commission.creator.username; // Adjust based on your user ID structure
  const isClient = currentUserId === commission.patron.username;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link to="/commissions" className="text-orange-600 hover:text-orange-700 flex items-center mb-4">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Commissions
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{commission.title}</h1>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            commission.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
            commission.status === 'ACCEPTED' ? 'bg-blue-100 text-blue-800' :
            commission.status === 'IN_PROGRESS' ? 'bg-purple-100 text-purple-800' :
            commission.status === 'DELIVERED' ? 'bg-teal-100 text-teal-800' :
            commission.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
            commission.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
            commission.status === 'DISPUTED' ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {commission.status}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{commission.description}</p>
          </div>

          {/* Requirements */}
          {commission.requirements && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Additional Requirements</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{commission.requirements}</p>
            </div>
          )}

          {/* Status Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Status Timeline</h2>
            <div className="space-y-4">
              {getStatusTimeline().map((step, index) => (
                <div key={index} className="flex items-start">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    step.active ? 'bg-orange-500' : 'bg-gray-300'
                  }`}>
                    {step.active ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="ml-4 flex-1">
                    <p className={`font-medium ${step.active ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.label}
                    </p>
                    {step.date && (
                      <p className="text-sm text-gray-600">
                        <LocalizedDate date={step.date} format="long" includeTime />
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Deliverables */}
          {commission.deliverables && commission.deliverables.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Deliverables</h2>
              <div className="space-y-3">
                {commission.deliverables.map((file) => (
                  <div key={file.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <svg className="w-8 h-8 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{file.fileName}</p>
                        <p className="text-sm text-gray-600">{formatFileSize(file.fileSize)} • {file.fileType}</p>
                        {file.description && (
                          <p className="text-sm text-gray-600 mt-1">{file.description}</p>
                        )}
                      </div>
                    </div>
                    <a
                      href={file.fileUrl}
                      download
                      className="ml-4 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium hover:bg-orange-200 transition-colors flex-shrink-0"
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reject Reason */}
          {commission.status === 'REJECTED' && commission.rejectReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="font-medium text-red-800 mb-2">Rejection Reason</h3>
              <p className="text-red-700">{commission.rejectReason}</p>
            </div>
          )}

          {/* Notes */}
          {commission.notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-medium text-blue-800 mb-2">Notes</h3>
              <p className="text-blue-700">{commission.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Commission Details Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-bold text-lg mb-4">Commission Details</h3>
            
            {/* Amount */}
            <div className="mb-4 pb-4 border-b">
              <p className="text-sm text-gray-600 mb-1">Budget</p>
              <p className="text-2xl font-bold text-orange-600">
                <LocalizedCurrency amount={commission.amount} currency={commission.currency} />
              </p>
              <p className="text-xs text-gray-500 mt-1">Platform fee: 5%</p>
            </div>

            {/* Deadline */}
            {commission.deadline && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">Deadline</p>
                <p className="font-medium text-gray-900">
                  <LocalizedDate date={commission.deadline} format="long" />
                </p>
              </div>
            )}

            {/* Created Date */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Requested</p>
              <p className="font-medium text-gray-900">
                <LocalizedDate date={commission.createdAt} format="long" />
              </p>
            </div>
          </div>

          {/* Parties Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-bold text-lg mb-4">Parties Involved</h3>
            
            {/* Client */}
            <div className="mb-4 pb-4 border-b">
              <p className="text-sm text-gray-600 mb-2">Client</p>
              <div className="flex items-center space-x-3">
                {commission.patron.avatar && (
                  <img
                    src={commission.patron.avatar}
                    alt={commission.patron.username}
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{commission.patron.username}</p>
                  <p className="text-sm text-gray-600">{commission.patron.email}</p>
                </div>
              </div>
            </div>

            {/* Creator */}
            <div>
              <p className="text-sm text-gray-600 mb-2">Creator</p>
              <div className="flex items-center space-x-3">
                {commission.creator.avatar && (
                  <img
                    src={commission.creator.avatar}
                    alt={commission.creator.username}
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{commission.creator.username}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-bold text-lg mb-4">Actions</h3>
            <div className="space-y-3">
              {/* Creator Actions */}
              {isCreator && (
                <>
                  {commission.status === 'PENDING' && (
                    <>
                      <button
                        onClick={handleAccept}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50"
                      >
                        Accept Commission
                      </button>
                      <button
                        onClick={() => setShowRejectModal(true)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                      >
                        Reject Commission
                      </button>
                    </>
                  )}

                  {commission.status === 'ACCEPTED' && (
                    <button
                      onClick={handleMarkInProgress}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
                    >
                      Mark In Progress
                    </button>
                  )}

                  {commission.status === 'IN_PROGRESS' && (
                    <button
                      onClick={handleMarkDelivered}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50"
                    >
                      Mark as Delivered
                    </button>
                  )}
                </>
              )}

              {/* Client Actions */}
              {isClient && (
                <>
                  {commission.status === 'DELIVERED' && (
                    <>
                      <button
                        onClick={handleApprove}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50"
                      >
                        Approve & Release Payment
                      </button>
                      <button
                        onClick={handleDispute}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium hover:bg-orange-200 transition-colors disabled:opacity-50"
                      >
                        Open Dispute
                      </button>
                    </>
                  )}

                  {['PENDING', 'ACCEPTED'].includes(commission.status) && (
                    <button
                      onClick={handleCancel}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                    >
                      Cancel Commission
                    </button>
                  )}
                </>
              )}

              {/* Common Actions */}
              {commission.status === 'DISPUTED' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    This commission is under dispute. Our support team will contact you soon.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Reject Commission</h3>
            <p className="text-gray-600 mb-4">Please provide a reason for rejecting this commission:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why you're rejecting this request..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : 'Reject Commission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
